import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Check, ChevronsUpDown, Search, Building2, User, Briefcase, Mail, Phone, IndianRupee } from 'lucide-react';
import { cn } from '../../lib/utils';
import { relationshipManagers, partnerOwners, productTypes } from '../data/mockData';
import { Comment } from '../../types/prospect';

// ── Searchable Select (merged from SearchableSelect.tsx) ────────────

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: string[];
  placeholder: string;
  allLabel: string;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder,
  allLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((option) =>
      option.toLowerCase().includes(searchLower)
    );
  }, [options, search]);

  const displayValue = value && value !== 'all'
    ? value
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn(!value || value === 'all' ? 'text-muted-foreground' : '')}>
            {displayValue}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0 z-[350]" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </div>
        <div
          className="max-h-[250px] overflow-y-auto overscroll-contain"
          onWheelCapture={(event) => event.stopPropagation()}
        >
          <div className="p-1">
            <button
              onClick={() => {
                onValueChange('all');
                setOpen(false);
                setSearch('');
              }}
              className={cn(
                'relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-2.5 text-sm outline-none hover:bg-accent/10 transition-colors',
                (!value || value === 'all') && 'bg-accent/10'
              )}
            >
              <Check
                className={cn(
                  'mr-2 h-4 w-4',
                  (!value || value === 'all') ? 'opacity-100' : 'opacity-0'
                )}
              />
              {allLabel}
            </button>
            {filteredOptions.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onValueChange(option);
                  setOpen(false);
                  setSearch('');
                }}
                className={cn(
                  'relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 px-2.5 text-sm outline-none hover:bg-accent/10 transition-colors',
                  value === option && 'bg-accent/10'
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === option ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {option}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="py-4 text-center text-sm text-muted-foreground">
                No results found
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Add Lead Dialog ─────────────────────────────────────────────────

interface AddLeadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddLead: (lead: NewLeadData) => void;
}

export interface NewLeadData {
  industryType: string;
  entityName: string;
  customerName: string;
  designation: string;
  email: string;
  phone: string;
  companySize: string;
  source: string;
  pastRelationshipName: string;
  leadRM: string;
  channelPartner: string;
  requirementType: string;
  product: string;
  dealSize: string;
  comments: Comment[];
}

const industryTypes = [
  'Manufacturing', 'Technology', 'Healthcare', 'Retail',
  'Financial Services', 'Logistics', 'Construction',
  'Agriculture', 'Energy', 'Education',
];

const companySizes = ['Small', 'Mid', 'Large'];

const sourceOptions = ['Referral', 'Social Media', 'Inbound', 'Outbound', 'Past Relationship'];

const pastRelationshipContacts = ['John Smith', 'Jane Doe', 'Robert Brown', 'Emily Davis', 'Michael Wilson'];

const requirementTypes = ['New', 'Renewal', 'Enhancement', 'Top-up'];

const initialFormData: NewLeadData = {
  industryType: '',
  entityName: '',
  customerName: '',
  designation: '',
  email: '',
  phone: '',
  companySize: '',
  source: '',
  pastRelationshipName: '',
  leadRM: '',
  channelPartner: '',
  requirementType: '',
  product: '',
  dealSize: '',
  comments: [],
};

export function AddLeadDialog({ isOpen, onClose, onAddLead }: AddLeadDialogProps) {
  const [formData, setFormData] = useState<NewLeadData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof NewLeadData, string>>>({});

  const handleChange = (field: keyof NewLeadData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof NewLeadData, string>> = {};
    if (!formData.industryType) newErrors.industryType = 'Required';
    if (!formData.entityName.trim()) newErrors.entityName = 'Required';
    if (!formData.customerName.trim()) newErrors.customerName = 'Required';
    if (!formData.designation.trim()) newErrors.designation = 'Required';
    if (!formData.email.trim()) {
      newErrors.email = 'Required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email';
    }
    if (!formData.leadRM) newErrors.leadRM = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onAddLead(formData);
      setFormData(initialFormData);
      onClose();
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white z-[400]">
        <DialogHeader className="pb-2 border-b border-border">
          <DialogTitle className="text-lg font-semibold text-foreground">Add New Lead</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-5 gap-y-4 py-4">
          {/* Industry Type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Industry Type <span className="text-destructive">*</span>
            </Label>
            <SearchableSelect
              value={formData.industryType}
              onValueChange={(v) => handleChange('industryType', v === 'all' ? '' : v)}
              options={industryTypes}
              placeholder="Select industry"
              allLabel="Select industry"
            />
            {errors.industryType && <p className="text-[11px] text-destructive">{errors.industryType}</p>}
          </div>

          {/* Entity Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Entity Name <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={formData.entityName}
                onChange={(e) => handleChange('entityName', e.target.value)}
                placeholder="Enter entity name"
                className={`h-9 pl-8 ${errors.entityName ? 'border-destructive' : ''}`}
              />
            </div>
            {errors.entityName && <p className="text-[11px] text-destructive">{errors.entityName}</p>}
          </div>

          {/* Customer Name */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Customer Name <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={formData.customerName}
                onChange={(e) => handleChange('customerName', e.target.value)}
                placeholder="Enter customer name"
                className={`h-9 pl-8 ${errors.customerName ? 'border-destructive' : ''}`}
              />
            </div>
            {errors.customerName && <p className="text-[11px] text-destructive">{errors.customerName}</p>}
          </div>

          {/* Designation */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Designation <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Briefcase className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={formData.designation}
                onChange={(e) => handleChange('designation', e.target.value)}
                placeholder="Enter designation"
                className={`h-9 pl-8 ${errors.designation ? 'border-destructive' : ''}`}
              />
            </div>
            {errors.designation && <p className="text-[11px] text-destructive">{errors.designation}</p>}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Email <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="Enter email"
                className={`h-9 pl-8 ${errors.email ? 'border-destructive' : ''}`}
              />
            </div>
            {errors.email && <p className="text-[11px] text-destructive">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Phone</Label>
            <div className="relative">
              <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="Enter phone number"
                className="h-9 pl-8"
              />
            </div>
          </div>

          {/* Requirement Type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Requirement Type</Label>
            <SearchableSelect
              value={formData.requirementType}
              onValueChange={(v) => handleChange('requirementType', v === 'all' ? '' : v)}
              options={requirementTypes}
              placeholder="Select type"
              allLabel="Select type"
            />
          </div>

          {/* Product */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Product</Label>
            <SearchableSelect
              value={formData.product}
              onValueChange={(v) => handleChange('product', v === 'all' ? '' : v)}
              options={productTypes}
              placeholder="Select product"
              allLabel="Select product"
            />
          </div>

          {/* Deal Size */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Deal Size (in Cr)</Label>
            <div className="relative">
              <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                type="number"
                step="0.1"
                min="0"
                value={formData.dealSize}
                onChange={(e) => handleChange('dealSize', e.target.value)}
                placeholder="e.g. 1.5"
                className="h-9 pl-8"
              />
            </div>
          </div>

          {/* Company Size */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Company Size</Label>
            <SearchableSelect
              value={formData.companySize}
              onValueChange={(v) => handleChange('companySize', v === 'all' ? '' : v)}
              options={companySizes}
              placeholder="Select size"
              allLabel="Select size"
            />
          </div>

          {/* Source */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Source</Label>
            <SearchableSelect
              value={formData.source}
              onValueChange={(v) => handleChange('source', v === 'all' ? '' : v)}
              options={sourceOptions}
              placeholder="Select source"
              allLabel="Select source"
            />
          </div>

          {/* Past Relationship Name - conditional */}
          {formData.source === 'Past Relationship' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Contact Name</Label>
              <SearchableSelect
                value={formData.pastRelationshipName}
                onValueChange={(v) => handleChange('pastRelationshipName', v === 'all' ? '' : v)}
                options={pastRelationshipContacts}
                placeholder="Select contact"
                allLabel="Select contact"
              />
            </div>
          )}

          {/* Lead RM */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Lead RM <span className="text-destructive">*</span>
            </Label>
            <SearchableSelect
              value={formData.leadRM}
              onValueChange={(v) => handleChange('leadRM', v === 'all' ? '' : v)}
              options={relationshipManagers}
              placeholder="Select RM"
              allLabel="Select RM"
            />
            {errors.leadRM && <p className="text-[11px] text-destructive">{errors.leadRM}</p>}
          </div>

          {/* Channel Partner */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Channel Partner</Label>
            <SearchableSelect
              value={formData.channelPartner}
              onValueChange={(v) => handleChange('channelPartner', v === 'all' ? '' : v)}
              options={partnerOwners}
              placeholder="Select partner"
              allLabel="Select partner"
            />
          </div>
        </div>

        <DialogFooter className="pt-2 border-t border-border">
          <Button variant="outline" onClick={handleClose} size="sm">
            Cancel
          </Button>
          <Button onClick={handleSubmit} size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground">
            Add Lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}